import React, { ReactElement, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDidUpdateEffect } from '@odin-labs/components';
import {
  useGetJobsiteInvitationQuery,
  useGetSelfOnboardingContainerSessionQuery,
} from 'apollo/generated/client-operations';
import { isEmpty } from 'utils';
import { useFlags } from 'featureFlags';
import {
  PolicyConsentGate,
  PolicyDocumentFragment,
  isPolicyAccepted,
} from 'containers/policyConsent';
import { localizeFactory } from 'utils/localization';
import { useLegacyState } from 'utils/useLegacyState';
import { useQueryParams } from 'utils/useQueryParams';
import { LoadingError } from 'components/loadingError';
import { defaultLanguage } from './utils';
import {
  SelfOnboardingState,
  SelfOnboardingStepComponent,
  SelfOnboardingStepKey,
} from './types';
import {
  LanguageSelectionStep,
  BasicInfoStep,
  ProfileStep,
  EmergencyContactStep,
  DocumentStep,
  MatchedWorkerStep,
  ErrorStep,
  ProfilePhotoStep,
  ReviewCompletedProfileStep,
  UnableToVerifyAccountStep,
  JobsiteWorkerWithDifferentContractorStep,
} from './steps';
import {
  AuthLoginStartStep,
  AuthLoginCompleteStep,
  AuthAccountCreationStep,
  AuthContractorSelectionStep,
} from './steps/authenticated';
import { getStepConfig } from './helpers/utils';
import { classes } from './SelfOnboardingContainer.style';
import { SelfOnboardingHeader } from './SelfOnboardingHeader';
import {
  getSelfOnboardingLanguageDependentState,
  getSelfOnboardingState,
} from './SelfOnboardingContainer.forms';
import { useAuthenticatedSelfOnboardingNavigation } from './helpers/useAuthenticatedSelfOnboardingNavigation';

// New-flow step map. Auth-critical steps (login / OTP / account creation /
// contractor selection) use the isolated authenticated components wired to the
// accountless OTP + Mutation A/B; the remaining steps reuse the shared
// presentational/flow components unchanged.
const componentsByStep: Record<
  SelfOnboardingStepKey,
  SelfOnboardingStepComponent
> = {
  [SelfOnboardingStepKey.LanguageSelection]: LanguageSelectionStep,
  [SelfOnboardingStepKey.Login]: AuthLoginStartStep,
  [SelfOnboardingStepKey.ConfirmLoginCode]: AuthLoginCompleteStep,
  [SelfOnboardingStepKey.WorkerName]: AuthAccountCreationStep,
  [SelfOnboardingStepKey.ContractorSelection]: AuthContractorSelectionStep,
  [SelfOnboardingStepKey.BasicInfo]: BasicInfoStep,
  [SelfOnboardingStepKey.WorkerConsentDocument]: DocumentStep,
  [SelfOnboardingStepKey.Profile]: ProfileStep,
  [SelfOnboardingStepKey.EmergencyContactInfo]: EmergencyContactStep,
  [SelfOnboardingStepKey.GovernmentIssuedId]: DocumentStep,
  [SelfOnboardingStepKey.NycSiteSafetyTrainingCard]: DocumentStep,
  [SelfOnboardingStepKey.OshaCard]: DocumentStep,
  [SelfOnboardingStepKey.TwicCard]: DocumentStep,
  [SelfOnboardingStepKey.JobsiteSafetyVideo]: DocumentStep,
  [SelfOnboardingStepKey.JobsiteSafetyDocument]: DocumentStep,
  [SelfOnboardingStepKey.LIRRBlueTraining]: DocumentStep,
  [SelfOnboardingStepKey.MustSafetyReport]: DocumentStep,
  [SelfOnboardingStepKey.ConfinedSpacesTraining]: DocumentStep,
  [SelfOnboardingStepKey.ProfilePhoto]: ProfilePhotoStep,
  [SelfOnboardingStepKey.ReviewCompletedProfile]: ReviewCompletedProfileStep,
  [SelfOnboardingStepKey.JobsiteWorkerWithDifferentContractor]:
    JobsiteWorkerWithDifferentContractorStep,
  [SelfOnboardingStepKey.AlreadyCompleted]: MatchedWorkerStep,
  [SelfOnboardingStepKey.Closed]: MatchedWorkerStep,
  [SelfOnboardingStepKey.UnableToVerifyAccount]: UnableToVerifyAccountStep,
  // Reachable only in the legacy flow; present to satisfy the exhaustive map.
  [SelfOnboardingStepKey.VerifyMobileDevice]: ErrorStep,
  [SelfOnboardingStepKey.ConfirmMobileDevice]: ErrorStep,
  [SelfOnboardingStepKey.VerifyMobileDeviceToSignIn]: ErrorStep,
  [SelfOnboardingStepKey.ConfirmMobileDeviceToSignIn]: ErrorStep,
};

const noHeaderSteps: SelfOnboardingStepKey[] = [
  SelfOnboardingStepKey.LanguageSelection,
];

const initialState: SelfOnboardingState = {} as SelfOnboardingState;

type AuthenticatedSelfOnboardingFlowProps = {
  jobsiteId?: string;
  contractorId?: string;
  currentPolicyDocuments?: PolicyDocumentFragment[];
};

/**
 * The isolated authenticated self-onboarding flow. Owns its own navigation
 * (step order + whitelist) and reads no feature flag — the route entry selects
 * this flow once. Native data model: pre-account steps carry no jobsite data
 * (there is no synthetic invitation); the full `getJobsiteInvitation` is loaded
 * only once the worker has a session.
 */
export function AuthenticatedSelfOnboardingFlow({
  jobsiteId,
  contractorId,
  currentPolicyDocuments,
}: AuthenticatedSelfOnboardingFlowProps): ReactElement {
  const { jobsiteInvitationId } = useParams<{
    jobsiteInvitationId: string;
    step: string;
  }>();
  const urlSearchParams = useQueryParams();
  const urlContractorId = urlSearchParams.get('contractorId') ?? contractorId;

  const [state, updateState] =
    useLegacyState<SelfOnboardingState>(initialState);
  const [language, setLanguage] = useState(defaultLanguage);

  const {
    data: selfOnboardingData,
    loading: selfOnboardingLoading,
    error: selfOnboardingError,
    refetch: refetchSelfOnboardingData,
  } = useGetSelfOnboardingContainerSessionQuery({
    fetchPolicy: 'no-cache',
    skip: !jobsiteId,
    variables: {
      // Scope the worker's jobsiteWorkers to this jobsite only — deliberately
      // NOT by contractor. On an enforce-contractor jobsite the invitation
      // carries the *default* contractor, but the worker's jobsiteWorker is
      // created under the real contractor they select in Mutation B. Filtering
      // by the invitation contractor would therefore hide that jobsiteWorker on
      // resume, leaving `jobsiteWorker` null so navigation can't fast-forward to
      // the persisted step and drops a returning (even completed) worker back
      // onto contractor selection. `getSelfOnboardingState` already resolves the
      // jobsiteWorker for this jobsite by jobsiteId.
      jobsiteWorkersInput: {
        jobsiteIds: [jobsiteId],
      },
      workerDocumentsInput: { jobsiteIds: [jobsiteId] },
    },
  });
  const selfOnboardingSession = selfOnboardingData?.getCurrentSession;
  const { user: selfOnboardingUser } = selfOnboardingSession ?? {};

  // Platform policy re-capture gate. A returning worker (existing account)
  // self-onboarding at a new site never passes through `AppLayout`, so the gate
  // is rendered here too — driven off this flow's own session query, since
  // `AuthContext`'s session isn't refreshed by the flow's mid-flight sign-in.
  // Brand-new accounts capture consent inline at creation, so their refreshed
  // session shows nothing outstanding and the gate stays closed.
  const { consentRecaptureGateEnabled } = useFlags();
  const policyConsentStatuses =
    selfOnboardingSession?.policyConsentStatuses ?? [];
  const hasOutstandingPolicyConsent = policyConsentStatuses.some(
    (status) => !isPolicyAccepted(status),
  );

  // The full invitation is post-account only: skipped (and unauthorized) until
  // the worker has a session. Pre-account steps consume no jobsite data.
  const {
    data: jobsiteInvitationData,
    loading: jobsiteInvitationLoading,
    error: jobsiteInvitationError,
  } = useGetJobsiteInvitationQuery({
    fetchPolicy: 'no-cache',
    skip: !selfOnboardingUser,
    variables: { jobsiteInvitationId },
  });
  const jobsiteInvitation = jobsiteInvitationData?.getJobsiteInvitation;
  const { jobsite } = jobsiteInvitation?.jobsiteContractor ?? {};

  const navigation = useAuthenticatedSelfOnboardingNavigation({
    jobsite,
    selfOnboardingState: state,
  });

  const { currentStep, currentConfigStep, state: navigationState } = navigation;
  const { isReview } = navigationState;

  React.useEffect(() => {
    if (!selfOnboardingData) return;
    if (selfOnboardingUser && jobsiteInvitation) {
      // Post-account: build the full onboarding state from the real invitation.
      updateState(
        getSelfOnboardingState({
          user: selfOnboardingUser,
          jobsiteInvitation,
          contractorId: urlContractorId,
          language,
        }),
      );
    } else if (!selfOnboardingUser) {
      // Pre-account: the session resolved with no worker. Seed the minimal state
      // the pre-account steps + navigation guard need (worker === null signals
      // "session done, unauthenticated"), preserving anything the steps staged.
      updateState({
        worker: null,
        userAccountId: null,
        jobsiteWorker: null,
        shouldSelectContractor: false,
      });
    }
  }, [
    selfOnboardingData,
    jobsiteInvitation,
    selfOnboardingUser,
    urlContractorId,
    // language // intentionally omitted — language-dependent state is rebuilt by
    // the separate useDidUpdateEffect below, so it must not re-trigger this full
    // state build (which would wipe fields the steps staged).
  ]);

  // Post-account language-dependent state (documents); no-op pre-account.
  useDidUpdateEffect(() => {
    if (selfOnboardingData && selfOnboardingUser && jobsiteInvitation) {
      updateState(
        getSelfOnboardingLanguageDependentState({
          user: selfOnboardingUser,
          jobsiteInvitation,
          language,
        }),
      );
    }
  }, [selfOnboardingData, jobsiteInvitation, language]);

  const stepConfig = React.useMemo(
    () => getStepConfig(jobsite?.modules, currentStep),
    [jobsite?.modules, currentStep],
  );
  const localize = localizeFactory(language.value);

  const loading =
    jobsiteInvitationLoading ||
    selfOnboardingLoading ||
    navigation.loading ||
    isEmpty(state);
  const error = jobsiteInvitationError || selfOnboardingError;

  if (!error && loading) {
    return <LoadingError loading={loading} />;
  }

  // Block the flow behind the unskippable consent gate while the authenticated
  // worker has outstanding consents; it self-clears once this flow's session
  // refetch shows none remain.
  if (consentRecaptureGateEnabled && hasOutstandingPolicyConsent) {
    return (
      <PolicyConsentGate
        policyConsentStatuses={policyConsentStatuses}
        onConsentRecorded={refetchSelfOnboardingData}
      />
    );
  }

  const getCurrentStepContent = (): ReactElement => {
    const StepComponent: SelfOnboardingStepComponent = error
      ? ErrorStep
      : componentsByStep[currentConfigStep];
    return (
      <StepComponent
        key={currentStep}
        loading={loading}
        refetchData={refetchSelfOnboardingData}
        user={selfOnboardingUser}
        state={state}
        updateState={updateState}
        navigation={navigation}
        jobsiteInvitation={jobsiteInvitation}
        stepConfig={stepConfig}
        language={language.value}
        onLanguageChange={setLanguage}
        localize={localize}
        currentPolicyDocuments={currentPolicyDocuments}
      />
    );
  };

  const withBackground =
    !error && currentStep === SelfOnboardingStepKey.LanguageSelection;
  const isHeaderVisible = !!error || !noHeaderSteps.includes(currentConfigStep);
  const canGoToPreviousStep =
    !error && !isReview && navigation.canGoToPreviousStep(currentStep);

  return (
    <div className={classes.fullContainer(withBackground)}>
      {isHeaderVisible && (
        <SelfOnboardingHeader
          language={language.value}
          onLanguageChange={setLanguage}
          localize={localize}
          navigation={navigation}
          canGoToPreviousStep={canGoToPreviousStep}
          state={state}
        />
      )}
      <div className={classes.container}>
        <div className={classes.stepContainer}>{getCurrentStepContent()}</div>
      </div>
    </div>
  );
}
